import {
  DEFAULT_COLORS,
  ensureContrast,
  getValidHexOrRgbaColor,
} from '@ardoq/color-helpers';
import { SvgShapePreview } from './SvgShapePreview';
import { getIcon } from '@ardoq/icons';
import { Header4, Paragraph, Text } from '@ardoq/typography';
import { ImagePreview } from './ImagePreview';
import styled, { css } from 'styled-components';
import { ComponentRepresentation } from '@ardoq/basic-components';
import { colors } from '@ardoq/design-tokens';
import { Stack, Inline } from '@ardoq/layout';
import { MetamodelEditorLabels, mergeMetamodelEditorLabels } from '../labels';

/** The default color for component labels and shapes elsewhere in the app. */
const defaultPlaceholderColor = DEFAULT_COLORS.level1;

export interface ComponentStylePreviewArgs {
  icon: string | null;
  color: string;
  shape: string | null;
  image?: string | null;
  /**
   * Optional partial overrides for user-facing strings rendered by the
   * preview. Missing keys fall back to English defaults.
   */
  labels?: Partial<MetamodelEditorLabels>;
}

const IconContainer = styled.span`
  ${({ color }) =>
    color &&
    getValidHexOrRgbaColor(color) &&
    css`
      > i {
        color: ${color};
      }

      > svg {
        fill: ${color};
        color: ${color};
      }
    `}
`;

export const ComponentStylePreview = ({
  icon,
  color = defaultPlaceholderColor,
  shape,
  image,
  labels: labelOverrides,
}: ComponentStylePreviewArgs) => {
  const labels = mergeMetamodelEditorLabels(labelOverrides);
  return (
    <Stack gap="medium">
      <Stack gap="small">
        <Header4>{labels.stylePreviewHeader}</Header4>
        <Stack gap="medium">
          <Paragraph variant="caption">{labels.stylePreviewCaption}</Paragraph>
          <Inline gap="xxsmall">
            <IconContainer
              style={{ color: ensureContrast(colors.white950, color) }}
            >
              <ComponentRepresentation
                icon={getIcon(icon)}
                isImage={false}
                value={''}
              />
            </IconContainer>
            <Text variant="text2">{getIcon(icon).label}</Text>
          </Inline>
        </Stack>
      </Stack>
      {image ? (
        <ImagePreview imageURI={image} imageAlt={labels.stylePreviewImageAlt} />
      ) : (
        <SvgShapePreview color={color} shapeId={shape || 'predefinedProcess'} />
      )}
    </Stack>
  );
};
